import { useCallback } from "react";
import {
    TaskListQuery,
    TaskListQuerySchema,
} from "@common/contracts/task.contract";
import { omit } from "@common/helpers/objects";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "../useWorkspaceUserCache";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "../useOrganizationUserCache";

const TASK_SESSION_FILTER_KEYS = ["search"] as const satisfies ReadonlyArray<
    keyof TaskListQuery
>;

export type TaskListFiltersCache = [
    TaskListQuery | undefined,
    (value: TaskListQuery | undefined) => void,
];

const sanitizeTaskListFilters = (
    filters: TaskListQuery | undefined,
): TaskListQuery | undefined => {
    if (!filters) {
        return undefined;
    }

    return omit(filters, [...TASK_SESSION_FILTER_KEYS]);
};

function useSanitizedTaskListFiltersCache([
    value,
    setter,
]: TaskListFiltersCache): TaskListFiltersCache {
    const setFilters = useCallback(
        (filters: TaskListQuery | undefined) => {
            setter(sanitizeTaskListFilters(filters));
        },
        [setter],
    );

    return [value, setFilters];
}

export function useTasksListFiltersCache(): TaskListFiltersCache {
    return useSanitizedTaskListFiltersCache(
        useWorkspaceUserCache<TaskListQuery>(
            WORKSPACE_USER_CACHE_KEYS.TASKS_PAGE_FILTERS_CACHE_KEY,
            TaskListQuerySchema,
        ),
    );
}

export function useOrganizationTasksListFiltersCache(): TaskListFiltersCache {
    return useSanitizedTaskListFiltersCache(
        useOrganizationUserCache<TaskListQuery>(
            ORGANIZATION_USER_CACHE_KEYS.CLIENTS_TASKS_PAGE_FILTERS_CACHE_KEY,
            TaskListQuerySchema,
        ),
    );
}
